import { AwsRum, AwsRumConfig } from "aws-rum-web";
import envConfig from "@/utils/awsRUM/config";

export const RUM_EVENT_TYPES = {
  // User Actions
  USER_ACTION: "UserAction",
  USER_INTERACTION: "UserInteraction",
  USER_LOGIN: "UserLogin",
  USER_LOGOUT: "UserLogout",

  // Navigation
  PAGE_VIEW: "PageView",
  ROUTE_CHANGE: "RouteChange",

  // API/Network
  API_REQUEST: "ApiRequest",
  API_RESPONSE: "ApiResponse",
  API_ERROR: "ApiError",

  // System Events
  SYSTEM_INIT: "SystemInitialized",
  SYSTEM_EVENT: "SystemEvent",
  PERFORMANCE_METRIC: "PerformanceMetric",
  FEATURE_FLAG: "FeatureFlag",

  // Standard Logs
  INFO: "Info",
  WARNING: "Warning",
  ERROR: "Error",
  DEBUG: "Debug",
} as const;

export type RumEventType = (typeof RUM_EVENT_TYPES)[keyof typeof RUM_EVENT_TYPES];

type RumEventData = Record<string, unknown>;

interface RumInitParams {
  domain: string;
  realm: string;
}

interface CustomEventOptions {
  type: RumEventType;
  message?: string;
  data?: RumEventData;
  timestamp?: string;
}

let rumClient: AwsRum | null = null;
let isInitialized = false;

/**
 * Console-fallback budget for `warn` in a NON-development build.
 *
 * `rumClient` is null in production on real misconfiguration paths — unknown or
 * empty `domain`/`realm`, a missing `envConfig[realm][domain]`, an `AwsRum`
 * constructor throw, or `init()` never being called. When that happens the app is
 * telemetry-dark and the console line is the only signal an on-call has, so the
 * fallback stays enabled in production. But it cannot stay UNBOUNDED there, for two
 * reasons that only apply to production:
 *
 *  1. **Volume.** Several `warn` sites are hot: `projectPuck` warns per failing rule
 *     per puck per projection pass, `isCrossDockPuck` has five per-puck sites,
 *     `ryteMessageProcessor` has seven per-WebSocket-record sites (one of which,
 *     the appointment `buildingCode` fallback, fires on a routine shape), and the
 *     slice warns on every version-gate rejection. An operator sits on one page for
 *     a whole shift, so "once per event" is effectively unbounded.
 *  2. **Retention.** `console.warn` keeps its arguments reachable so DevTools can
 *     expand them later — even if DevTools is never opened. Passing the payload
 *     OBJECT therefore pins whatever it references for the life of the tab, and two
 *     call sites deliberately pass a whole API row (`ryteDataSlice`'s two
 *     "missing ID" seed warns log `{ row }`, kept on purpose because the whole row
 *     is what you want when debugging a malformed payload).
 *
 * So production gets a capped number of lines with the payload SERIALIZED to a
 * string — informative, and holding no reference to the caller's object. Local
 * development keeps the live object, because expanding it in DevTools is the entire
 * point there and neither concern above applies.
 */
const PROD_WARN_CAP = 20;

/** Per-line payload ceiling for the production console. A whole API row can be KBs. */
const PROD_WARN_PAYLOAD_CHARS = 2000;

let prodWarnCount = 0;

/** Read at call time, not module load, so tests can exercise both branches. */
const isDevEnvironment = (): boolean => process.env.NODE_ENV === "development";

/**
 * Render a warn payload as a bounded STRING for the production console.
 *
 * A string is the point: it copies the values out, so the console retains no
 * reference to the caller's object graph. Cycles are tolerated (entity rows can
 * contain them) and the result is truncated rather than dropped, because a
 * truncated payload still names the failing entity.
 */
const serializeWarnPayload = (data: RumEventData | undefined): string => {
  if (data === undefined) return "{}";
  const seen = new WeakSet<object>();
  let out: string;
  try {
    out =
      JSON.stringify(data, (_key, value) => {
        if (typeof value === "object" && value !== null) {
          if (seen.has(value as object)) return "[Circular]";
          seen.add(value as object);
        }
        return value as unknown;
      }) ?? "undefined";
  } catch {
    // BigInt, a throwing getter, a `toJSON` that throws — never let telemetry
    // formatting break the caller.
    return "[unserializable payload]";
  }
  if (out.length <= PROD_WARN_PAYLOAD_CHARS) return out;
  return `${out.slice(0, PROD_WARN_PAYLOAD_CHARS)}…[truncated ${
    out.length - PROD_WARN_PAYLOAD_CHARS
  } chars]`;
};

function initializeAwsRum({ domain, realm }: RumInitParams): AwsRum | null {
  try {
    // Skip RUM in development — no real-user telemetry from local dev sessions.
    // The client stays null so every record* call no-ops via its guard.
    if (process.env.NODE_ENV === "development") {
      console.log("RUM initialization skipped in development environment");
      return null;
    }

    if (isInitialized || rumClient) {
      console.warn("RUM client already initialized");
      return rumClient;
    }

    if (!domain || domain === "unknown" || !realm || realm === "unknown") {
      console.warn("Invalid domain or realm for RUM initialization");
      return null;
    }

    const rumConfigInfo = envConfig[realm]?.[domain];

    if (!rumConfigInfo) {
      console.error(`No RUM configuration found for realm: ${realm}, domain: ${domain}`);
      return null;
    }

    const { config, APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION } = rumConfigInfo;

    rumClient = new AwsRum(
      APPLICATION_ID,
      APPLICATION_VERSION,
      APPLICATION_REGION,
      config as AwsRumConfig
    );

    isInitialized = true;
    return rumClient;
  } catch (error) {
    console.error("Failed to initialize RUM client:", error);
    return null;
  }
}

class RumService {
  private static instance: RumService;
  private lastPageViewTime = 0;

  static getInstance(): RumService {
    if (!RumService.instance) {
      RumService.instance = new RumService();
    }
    return RumService.instance;
  }

  init(data: RumInitParams): void {
    initializeAwsRum(data);
  }

  setSessionMetadata(metadata: any): void {
    if (!rumClient) {
      console.warn("RUM client not initialized");
      return;
    }

    try {
      rumClient.addSessionAttributes(metadata);
    } catch (error) {
      console.error("Failed to set RUM session metadata:", error);
    }
  }

  recordError(e: ErrorEvent | string | Error): void {
    if (!rumClient) {
      console.warn("RUM client not initialized");
      return;
    }
    try {
      rumClient.recordError(e);
    } catch (error) {
      console.error("Failed to record error in RUM:", error);
    }
  }

  recordPageView(pageId: string): void {
    if (!rumClient) {
      console.warn("RUM client not initialized");
      return;
    }
    try {
      rumClient.recordPageView(pageId);
    } catch (error) {
      console.error("Failed to record page view in RUM:", error);
    }
  }

  customEvent(options: CustomEventOptions): void {
    if (!rumClient) {
      // Deliberately silent. This is the analytics funnel — `trackRyteEvent`
      // and friends reach it from ~90 sites, and `info()` fires once per
      // WebSocket message. A fallback here emitted a payload-free
      // "RUM client not initialized" line hundreds of times per session,
      // which buried the diagnostics that actually want reading. `warn()`
      // and `error()` carry their own fallbacks instead.
      return;
    }

    const { type, message, data = {}, timestamp = new Date().toISOString() } = options;

    try {
      rumClient.recordEvent(type, {
        message,
        timestamp,
        ...data,
      });
    } catch (error) {
      console.error(`Failed to record custom event (${type}) in RUM:`, error);
    }
  }

  /**
   * Informational telemetry. **No console fallback by design** — this fires
   * per-WebSocket-message in RYTE, so echoing it locally would flood the very
   * console `warn()` is trying to make readable. See `customEvent`.
   */
  info(message: string, data?: RumEventData): void {
    this.customEvent({
      type: RUM_EVENT_TYPES.INFO,
      message,
      data,
    });
  }

  /**
   * Diagnostic warning. Emits to the console when the RUM client is absent, so
   * the message AND its payload survive — `customEvent` drops both.
   *
   * Deliberately NOT gated to development only: `rumClient` is also null in
   * production on misconfiguration, and that is exactly when an on-call needs a
   * local signal. Production is BOUNDED instead of silenced — capped line count,
   * payload serialized to a string so nothing is retained. See `PROD_WARN_CAP`
   * for the full rationale, and keep the two branches distinct: collapsing them
   * either floods a production tab or blinds local development.
   *
   * The RUM record itself (`customEvent` below) is unaffected by the cap — the
   * budget governs the console only.
   */
  warn(message: string, data?: RumEventData): void {
    if (!rumClient) {
      if (isDevEnvironment()) {
        console.warn(`[rum] ${message}`, data ?? {});
      } else if (prodWarnCount < PROD_WARN_CAP) {
        prodWarnCount += 1;
        console.warn(`[rum] ${message}`, serializeWarnPayload(data));
      } else if (prodWarnCount === PROD_WARN_CAP) {
        // Exactly once: the increment past the cap makes this branch unreachable
        // afterwards, so the notice cannot itself become the flood.
        prodWarnCount += 1;
        console.warn(
          `[rum] RUM client unavailable — further warnings suppressed after ${PROD_WARN_CAP} lines`
        );
      }
    }
    this.customEvent({
      type: RUM_EVENT_TYPES.WARNING,
      message,
      data,
    });
  }

  /**
   * Log an error to RUM with structured context.
   *
   * `context` (optional) is merged into the RUM custom event payload
   * alongside the auto-extracted `errorMessage`/`errorStack`. Use it for
   * structured telemetry keys like `goalId`, `componentStack`, `visitId`
   * — anything you'd want to filter/group by in CloudWatch. Keep values
   * primitive and small.
   *
   * See `.claude/steering/architecture/analytics-tracking.md` § "RUM Error
   * Logging" for the canonical usage pattern.
   */
  error(
    message: string,
    error: Error | ErrorEvent | string,
    context?: Record<string, unknown>
  ): void {
    // `context` is included so the structured keys the steering doc tells
    // callers to pass (`goalId`, `visitId`, …) survive to the console, not
    // just to the RUM payload below. Omitted entirely when absent so the
    // existing two-arg call signature is unchanged for callers without context.
    if (context) {
      console.error(message, error, context);
    } else {
      console.error(message, error);
    }
    if (!rumClient) {
      return;
    }

    try {
      rumClient.recordError(error);

      this.customEvent({
        type: RUM_EVENT_TYPES.ERROR,
        message,
        data: {
          errorMessage: error instanceof Error ? error.message : String(error),
          errorStack: error instanceof Error ? error.stack : undefined,
          ...context,
        },
      });
    } catch (recordError) {
      console.error("Failed to record error in RUM:", recordError);
    }
  }

  isInitialized(): boolean {
    return isInitialized && rumClient !== null;
  }

  recordRouteChange(
    pathname: string,
    previousPathname?: string,
    additionalData?: Record<string, any>
  ): void {
    const currentTime = Date.now();
    const timeOnPreviousPage = this.lastPageViewTime ? currentTime - this.lastPageViewTime : 0;

    this.customEvent({
      type: RUM_EVENT_TYPES.ROUTE_CHANGE,
      message: `Navigation to ${pathname}`,
      data: {
        currentPath: pathname,
        previousPath: previousPathname,
        timeOnPreviousPage, // Time spent on previous page in ms
        timestamp: new Date().toISOString(),
        referrer: document.referrer,
        searchParams: window.location.search,
        hash: window.location.hash,
        navigationTiming: this.getNavigationTiming(),
        ...additionalData,
      },
    });
    // Update last page view time
    this.lastPageViewTime = currentTime;
    // Record page view
    this.recordPageView(pathname);
  }

  private getNavigationTiming() {
    if (window.performance && window.performance.timing) {
      const timing = window.performance.timing;
      return {
        loadTime: timing.loadEventEnd - timing.navigationStart,
        domReadyTime: timing.domContentLoadedEventEnd - timing.navigationStart,
        renderTime: timing.domComplete - timing.domLoading,
      };
    }
    return null;
  }
}

// Export singleton instance
export default RumService.getInstance();
